import { Box, Button, CircularProgress, Typography } from '@material-ui/core'
import { FlexHorizontal, Spacer, useToasts } from '@vestaboard/installables'
import React, { useEffect, useState } from 'react'

import { Alert } from '@mui/material'
import { FeaturedItem } from './components/FeaturedItem'
import { useGetMarketplaceGallery } from './hooks/useGetMarketplaceGallery'
import { useListMarketplaceListingsV2 } from '../marketplace-listings/hooks/useListMarketplaceListingsV2'
import { useUpdateMarketplaceGallery } from './hooks/useUpdateMarketplaceGallery'

interface IFeaturedListing {
  id?: string | null
  image?: string | null
  imageMedia?: string | null
  variantImageMediaCdnUrl?: string | null
  variantImageMedia?: string | null
  description?: string | null
}

interface IMarketplaceGalleryProps {
  isNote?: boolean
}

export const MarketplaceGallery = (props: IMarketplaceGalleryProps) => {
  const { addToast } = useToasts()
  const { data, loading, error } = useGetMarketplaceGallery({ isNote: props.isNote ?? false })
  const [featuredListings, setFeaturedListings] = useState<IFeaturedListing[]>([])
  const [originalFeaturedListings, setOriginalFeaturedListings] = useState<IFeaturedListing[]>([])
  const [save, { loading: saving }] = useUpdateMarketplaceGallery()
  const { data: listingsData } = useListMarketplaceListingsV2(
    {
      input: {
        limit: 1000,
        ...(props?.isNote
          ? { supportsNote: true }
          : {
              supportsFlagship: true
            })
      }
    },
    true
  )
  const hasChanges = JSON.stringify(featuredListings) !== JSON.stringify(originalFeaturedListings)

  const canSave = hasChanges
  const options = (listingsData?.listMarketplaceListingsV2?.marketplaceListings || []).map(listing => ({
    label: listing.title,
    id: listing.id
  }))

  useEffect(() => {
    if (!!featuredListings.length) {
      return
    }
    if (data?.marketplaceGalleryV2?.__typename === 'MarketplaceGalleryV2') {
      const items = data.marketplaceGalleryV2.items.map(item => ({
        id: item.marketplaceListing?.id || null,
        image: item.image || null,
        description: item.description || null
      }))
      setFeaturedListings(items)
      setOriginalFeaturedListings(items)
    }
  }, [data, featuredListings.length])

  return (
    <Box>
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>{props.isNote ? 'Note' : 'Flagship'} Marketplace Features</Typography>
      </FlexHorizontal>
      <Spacer size='large' />
      {error || data?.marketplaceGalleryV2?.__typename === 'MarketplaceGalleryErrorV2' ? (
        <Alert severity='error'>There was an error loading the marketplace features</Alert>
      ) : loading || !data ? (
        <CircularProgress />
      ) : (
        <>
          <Box display='flex' flexWrap='wrap'>
            {featuredListings.map((featuredListing, index) => (
              <FeaturedItem
                key={featuredListing?.id || index}
                isNote={props.isNote}
                options={options}
                id={featuredListing.id ?? null}
                image={featuredListing.image ?? null}
                imagePreviewUrl={featuredListing?.image ?? null}
                description={featuredListing.description ?? null}
                aspectRatio={2 / 1}
                onChangeId={id =>
                  setFeaturedListings([
                    ...featuredListings.slice(0, index),
                    {
                      id,
                      image: null,
                      description: null
                    },
                    ...featuredListings.slice(index + 1)
                  ])
                }
                onChangeImage={image => {
                  setFeaturedListings([
                    ...featuredListings.slice(0, index),
                    {
                      ...featuredListings[index],
                      image
                    },
                    ...featuredListings.slice(index + 1)
                  ])
                }}
                onChangeDescription={description =>
                  setFeaturedListings([
                    ...featuredListings.slice(0, index),
                    {
                      ...featuredListings[index],
                      description
                    },
                    ...featuredListings.slice(index + 1)
                  ])
                }
                onDelete={() =>
                  setFeaturedListings([...featuredListings.slice(0, index), ...featuredListings.slice(index + 1)])
                }
              />
            ))}
          </Box>
          <Spacer size='large' />
          <Button onClick={() => setFeaturedListings([...featuredListings, { id: null, image: null }])}>Add</Button>
          <Button
            onClick={async () => {
              const result = await save({
                variables: {
                  input: {
                    isNote: props.isNote ?? false,
                    items: featuredListings.map(featuredListing => ({
                      id: featuredListing.id || '',
                      marketplaceListingId: featuredListing.id || '',
                      description: featuredListing.description || ''
                    }))
                  }
                }
              })

              if (result.data?.updateMarketplaceGalleryV2?.__typename !== 'MarketplaceGalleryV2') {
                addToast('Error saving marketplace features', { appearance: 'error' })
                return
              }
              setOriginalFeaturedListings(featuredListings)
              addToast('Marketplace features saved successfully', { appearance: 'success' })
            }}
            disabled={!canSave || saving}
            variant='contained'
            color='primary'>
            Save
          </Button>
          <Spacer size='large' />
        </>
      )}
    </Box>
  )
}
